'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard, CalendarDays, Users, Wrench,
  BarChart3, Settings, ChevronLeft, ChevronRight,
  Bell, LogOut, Shield
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';


const navItems = [
  { key: 'nav-dashboard', href: '/admin-dashboard', icon: LayoutDashboard, label: 'Dashboard', badge: null },
  { key: 'nav-bookings', href: '/admin-bookings', icon: CalendarDays, label: 'Bookings', badge: null },
  { key: 'nav-clients', href: '/admin-clients', icon: Users, label: 'Clients', badge: null },
  { key: 'nav-services', href: '/admin-services', icon: Wrench, label: 'Services', badge: null },
  { key: 'nav-reports', href: '/admin-reports', icon: BarChart3, label: 'Reports', badge: null },
];

const bottomItems = [
  { key: 'nav-notifications', href: '/admin-notifications', icon: Bell, label: 'Notifications', badge: null },
  { key: 'nav-settings', href: '/admin-settings', icon: Settings, label: 'Settings', badge: null },
];

export default function AdminSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();
  const { signOut } = useAuth();

  return (
    <aside
      className={`fixed left-0 top-0 bottom-0 z-40 flex flex-col bg-card border-r border-border transition-all duration-300 ease-in-out ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`h-16 flex items-center border-b border-border px-4 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && (
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-base tracking-tight">
              Precision Surface & Detail <span className="gold-text">Booking</span>
            </span>
          </Link>
        )}
        {collapsed && (
          <Link href="/">
            <AppLogo size={28} />
          </Link>
        )}
        {!collapsed && (
          <button
            onClick={() => setCollapsed(true)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft size={16} />
          </button>
        )}
      </div>
      {/* Admin badge */}
      {!collapsed && (
        <div className="mx-3 mt-3 mb-1 px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 flex items-center gap-2">
          <Shield size={14} className="text-primary" />
          <span className="text-xs font-semibold text-primary">Admin Panel</span>
        </div>
      )}
      {/* Nav items */}
      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto scrollbar-thin">
        {navItems?.map((item) => {
          const Icon = item?.icon;
          const isActive = pathname === item?.href;
          return (
            <Link
              key={item?.key}
              href={item?.href}
              className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 relative ${
                isActive
                  ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/40'
              }`}
              title={collapsed ? item?.label : undefined}
            >
              <Icon size={18} className="flex-shrink-0" />
              {!collapsed && (
                <span className="text-sm font-medium flex-1">{item?.label}</span>
              )}
              {!collapsed && item?.badge && (
                <span className="px-1.5 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-semibold tabular-nums">
                  {item?.badge}
                </span>
              )}
              {collapsed && item?.badge && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary" />
              )}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 rounded-md bg-card border border-border text-xs font-medium text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-card">
                  {item?.label}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
      {/* Bottom items */}
      <div className="px-2 pb-3 space-y-0.5 border-t border-border pt-3">
        {bottomItems?.map((item) => {
          const Icon = item?.icon;
          return (
            <Link
              key={item?.key}
              href={item?.href}
              className="group flex items-center gap-3 px-3 py-2.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-150 relative"
              title={collapsed ? item?.label : undefined}
            >
              <Icon size={18} className="flex-shrink-0" />
              {!collapsed && <span className="text-sm font-medium flex-1">{item?.label}</span>}
              {!collapsed && item?.badge && (
                <span className="px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-semibold tabular-nums">
                  {item?.badge}
                </span>
              )}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 rounded-md bg-card border border-border text-xs font-medium text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-card">
                  {item?.label}
                </span>
              )}
            </Link>
          );
        })}

        {/* User */}
        <div className={`flex items-center gap-3 px-3 py-2.5 mt-2 rounded-lg border border-border bg-muted/20 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-full gold-gradient flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-primary-foreground">MR</span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">Westin DeGrasso</p>
              <p className="text-xs text-muted-foreground truncate">Owner</p>
              <p className="text-xs text-muted-foreground truncate">St. Louis, MO</p>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={() => signOut()}
              className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
              title="Sign out"
            >
              <LogOut size={14} />
            </button>
          )}
        </div>

        {/* Expand button when collapsed */}
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-150 mt-1"
            aria-label="Expand sidebar"
          >
            <ChevronRight size={16} />
          </button>
        )}
      </div>
    </aside>
  );
}